import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as z from 'zod';

import { ILogger } from '@shure/shared/angular/utils/logging';

const GO_TO_STEP_STORAGE_KEY = 'shure.claiming.goToQrClaimingStep';
const SCANNED_CODES_STORAGE_KEY = 'shure.claiming.scannedQrCodes';

const scannedQrCodeSchema = z.object({
	mac: z.string(),
	serial: z.string()
});

const scannedQrCodesSchema = z.array(scannedQrCodeSchema);

export type ScannedQrCode = z.infer<typeof scannedQrCodeSchema>;

export enum SaveQrCodeResult {
	Success,
	Duplicate
}

@Injectable({ providedIn: 'root' })
export class QrCodeClaimingService {
	private readonly logger: ILogger;
	private readonly scannedCodes$ = new BehaviorSubject<ScannedQrCode[]>(this.getScannedQrCodes());

	constructor(logger: ILogger) {
		this.logger = logger.createScopedLogger('QrCodeClaimingService');
	}

	/**
	 * Sets a flag in session storage to indicate that the user should be immediately taken to the
	 * QR code claiming step.
	 */
	public setGoToQrClaimingStepFlag(): void {
		sessionStorage.setItem(GO_TO_STEP_STORAGE_KEY, 'true');
	}

	/**
	 * Reads the go to QR claiming step flag from local storage and returns a boolean indicating whether
	 * or not the flag was set. Discards the flag after reading it.
	 */
	public readGoToQrClaimingStepFlag(): boolean {
		const shouldGoToStep = sessionStorage.getItem(GO_TO_STEP_STORAGE_KEY) !== null;
		sessionStorage.removeItem(GO_TO_STEP_STORAGE_KEY);
		return shouldGoToStep;
	}

	/**
	 * Return an observable that emits the full array of scanned codes every time a new value is stored.
	 */
	public getScannedQrCodes$(): Observable<ScannedQrCode[]> {
		return this.scannedCodes$.asObservable();
	}

	/**
	 * Gets the current value of the scanned codes storage item.
	 */
	public getScannedQrCodes(): ScannedQrCode[] {
		try {
			const scannedCodesJson = sessionStorage.getItem(SCANNED_CODES_STORAGE_KEY);
			return scannedCodesJson ? scannedQrCodesSchema.parse(JSON.parse(scannedCodesJson)) : [];
		} catch (e) {
			// If the stored QR codes are in an erroneous format, log an error message and clear the current value.
			this.logger.error('getScannedQrCodes', 'Unable to parse stored QR codes. Clearing cache.', e);
			sessionStorage.removeItem(SCANNED_CODES_STORAGE_KEY);
			return [];
		}
	}

	/**
	 * Saves the provided scanned code in session storage.
	 */
	public saveScannedQrCode(code: ScannedQrCode): SaveQrCodeResult {
		const scannedCodes = this.getScannedQrCodes();

		// If QR code with this MAC/serial combo has already been stored, return
		if (scannedCodes.some((savedCode) => savedCode.mac === code.mac && savedCode.serial === code.serial)) {
			return SaveQrCodeResult.Duplicate;
		}

		scannedCodes.push(code);
		sessionStorage.setItem(SCANNED_CODES_STORAGE_KEY, JSON.stringify(scannedCodes));
		this.scannedCodes$.next(scannedCodes);
		return SaveQrCodeResult.Success;
	}

	/**
	 * Clears the go to QR claiming step flag and the scanned codes from session storage.
	 */
	public clear(): void {
		sessionStorage.removeItem(GO_TO_STEP_STORAGE_KEY);
		sessionStorage.removeItem(SCANNED_CODES_STORAGE_KEY);
		this.scannedCodes$.next([]);
	}
}
